import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { TrendingDown, Trash2, Recycle, BarChart3, Ship, Award, Coins } from "lucide-react";
import { toast } from "sonner";

export default function WasteReport() {
  const [report, setReport] = useState(null);

  const loadReport = useCallback(async () => {
    try {
      const res = await api.getWasteReport();
      setReport(res.data);
    } catch { toast.error("Kunne ikke laste rapport"); }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  if (!report) return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-stone-200 rounded w-48" />
        <div className="h-32 bg-stone-100 rounded-xl" />
      </div>
    </div>
  );

  const weekKeys = Object.keys(report.weekly_summary || {}).sort().reverse();
  const totalUsed = weekKeys.reduce((sum, k) => sum + report.weekly_summary[k].used.length, 0);
  const totalWasted = weekKeys.reduce((sum, k) => sum + report.weekly_summary[k].wasted.length, 0);
  const wastePercent = totalUsed + totalWasted > 0 ? Math.round((totalWasted / (totalUsed + totalWasted)) * 100) : 0;
  const trips = report.trips || [];
  const topCategories = report.top_categories || [];
  const topItems = report.top_items || [];
  const wasteCostCategories = report.waste_cost_categories || [];
  const wasteCostItems = report.waste_cost_items || [];
  const wasteCostTotal = Number(report.waste_cost_total_nok || 0);
  const wasteHasCost = !!report.waste_cost_has_data;
  const maxCatCount = topCategories[0]?.count || 1;
  const maxItemCount = topItems[0]?.count || 1;
  const maxCatCost = wasteCostCategories[0]?.cost_nok || 1;
  const fmtNok = (n) => new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(Number(n) || 0);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="waste-report-page">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Matsvinn-rapport</h1>
        <p className="text-sm text-stone-500 mt-1">Oversikt over matforbruk og svinn per tur og uke</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white border border-stone-100 rounded-xl p-5" data-testid="stat-expired">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><Trash2 className="h-5 w-5 text-red-500" /></div>
            <span className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Utgått i lager</span>
          </div>
          <span className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{report.expired_in_pantry}</span>
          <span className="text-xs text-stone-400 ml-1">varer</span>
        </div>
        <div className="bg-white border border-stone-100 rounded-xl p-5" data-testid="stat-withdrawals">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-sailcloth-100 flex items-center justify-center"><Recycle className="h-5 w-5 text-seawater-500" /></div>
            <span className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Totalt forbruk</span>
          </div>
          <span className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{report.total_withdrawals}</span>
          <span className="text-xs text-stone-400 ml-1">uttak</span>
        </div>
        <div className="bg-white border border-stone-100 rounded-xl p-5" data-testid="stat-wasted">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><TrendingDown className="h-5 w-5 text-amber-500" /></div>
            <span className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Registrert svinn</span>
          </div>
          <span className="text-3xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{totalWasted}</span>
          <span className="text-xs text-stone-400 ml-1">varer</span>
        </div>
        <div className="bg-white border border-stone-100 rounded-xl p-5" data-testid="stat-percent">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center"><BarChart3 className="h-5 w-5 text-stone-500" /></div>
            <span className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Svinn %</span>
          </div>
          <span className={`text-3xl font-bold ${wastePercent > 20 ? "text-red-600" : wastePercent > 10 ? "text-amber-600" : "text-seawater-600"}`} style={{ fontFamily: "'Cormorant Garamond', serif" }}>{wastePercent}%</span>
        </div>
      </div>

      {/* Per-trip breakdown */}
      {trips.length > 0 && (
        <div className="mb-8" data-testid="trips-section">
          <h2 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3 flex items-center gap-2"><Ship className="h-3.5 w-3.5" />Per tur</h2>
          <div className="bg-white border border-stone-100 rounded-xl divide-y divide-stone-100">
            {trips.map((t) => (
              <div key={t.trip_id || "no-trip"} className="px-4 py-3" data-testid={`trip-row-${t.trip_id || "notrip"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="min-w-0">
                    <span className="text-sm font-semibold text-stone-700 truncate block">{t.trip_name || "Uten tur"}</span>
                    <span className="text-[11px] text-stone-400">{t.first_date} → {t.last_date}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs flex-shrink-0">
                    <span className="text-seawater-600">{t.used} brukt</span>
                    <span className="text-red-500">{t.wasted} svinn</span>
                    <span className={`font-semibold ${t.waste_percent > 20 ? "text-red-600" : t.waste_percent > 10 ? "text-amber-600" : "text-seawater-600"}`}>{t.waste_percent}%</span>
                  </div>
                </div>
                <div className="w-full bg-stone-100 rounded-full h-2 flex overflow-hidden">
                  <div className="bg-seawater-500 h-2" style={{ width: `${t.total ? (t.used / t.total) * 100 : 0}%` }} />
                  <div className="bg-red-400 h-2" style={{ width: `${t.total ? (t.wasted / t.total) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waste cost rollup */}
      {wasteHasCost && wasteCostTotal > 0 && (
        <div className="mb-8" data-testid="waste-cost-section">
          <h2 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3 flex items-center gap-2"><Coins className="h-3.5 w-3.5" />Matsvinn-kost (estimert frå kvitteringar)</h2>
          <div className="bg-gradient-to-br from-red-50 to-amber-50 border border-red-100 rounded-xl p-5 mb-4">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <span className="text-xs uppercase tracking-wider text-stone-500 font-semibold block">Total estimert kost</span>
                <span className="text-4xl font-bold text-red-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{fmtNok(wasteCostTotal)}</span>
              </div>
              <p className="text-xs text-stone-500 max-w-xs">Estimert ut frå snittprisar i dine kvitteringar. Faktiske kostnadar kan variere.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {wasteCostCategories.length > 0 && (
              <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="waste-cost-cat">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">Kost per kategori</h3>
                <div className="space-y-2">
                  {wasteCostCategories.map((c) => (
                    <div key={c.category} className="flex items-center gap-3" data-testid={`waste-cost-cat-${c.category}`}>
                      <span className="text-sm text-stone-700 w-32 truncate">{c.category}</span>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="bg-red-400 h-2 rounded-full" style={{ width: `${(c.cost_nok / maxCatCost) * 100}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-red-600 w-20 text-right">{fmtNok(c.cost_nok)}</span>
                      <span className="text-[10px] text-stone-400 w-8 text-right">{c.count}×</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {wasteCostItems.length > 0 && (
              <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="waste-cost-items">
                <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">Dyraste sløsing</h3>
                <div className="space-y-2">
                  {wasteCostItems.slice(0, 8).map((it) => (
                    <div key={it.name} className="flex items-center gap-3" data-testid={`waste-cost-item-${it.name}`}>
                      <span className="text-sm text-stone-700 flex-1 truncate">{it.name}</span>
                      <span className="text-xs font-semibold text-red-600">{fmtNok(it.cost_nok)}</span>
                      <span className="text-[10px] text-stone-400 w-8 text-right">{it.count}×</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top categories + items */}
      {(topCategories.length > 0 || topItems.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {topCategories.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="top-categories">
              <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3 flex items-center gap-2"><Award className="h-3.5 w-3.5" />Svinn per kategori</h3>
              <div className="space-y-2">
                {topCategories.map((c) => (
                  <div key={c.category} className="flex items-center gap-3" data-testid={`cat-${c.category}`}>
                    <span className="text-sm text-stone-700 w-32 truncate">{c.category}</span>
                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="bg-red-400 h-2 rounded-full" style={{ width: `${(c.count / maxCatCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-stone-600 w-6 text-right">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {topItems.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-xl p-4" data-testid="top-items">
              <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3 flex items-center gap-2"><Trash2 className="h-3.5 w-3.5" />Mest kastet</h3>
              <div className="space-y-2">
                {topItems.map((it) => (
                  <div key={it.name} className="flex items-center gap-3" data-testid={`item-${it.name}`}>
                    <span className="text-sm text-stone-700 flex-1 truncate">{it.name}</span>
                    <div className="w-24 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="bg-amber-400 h-2 rounded-full" style={{ width: `${(it.count / maxItemCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-stone-600 w-6 text-right">{it.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Expired items in pantry */}
      {report.expired_items.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold mb-3">Utgåtte varer i beholdningen</h2>
          <div className="bg-white border border-red-100 rounded-xl divide-y divide-red-50">
            {report.expired_items.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3" data-testid={`expired-${item.id}`}>
                <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-sm font-medium text-stone-700">{item.name}</span>
                  {(item.amount || item.unit) && <span className="text-xs text-stone-400 ml-2">{item.amount} {item.unit}</span>}
                </div>
                <span className="text-xs text-red-500">Utløpt: {item.expiry_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly breakdown */}
      <div>
        <h2 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">Ukentlig oversikt</h2>
        {weekKeys.length === 0 ? (
          <div className="text-center py-12 bg-white border border-stone-100 rounded-xl" data-testid="waste-empty">
            <BarChart3 className="h-10 w-10 mx-auto text-stone-300 mb-3" />
            <p className="text-sm text-stone-400">Ingen data ennå. Registrer uttak i beholdningen for å se statistikk.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {weekKeys.map((week) => {
              const data = report.weekly_summary[week];
              const total = data.used.length + data.wasted.length;
              const usedPct = total > 0 ? Math.round((data.used.length / total) * 100) : 100;
              return (
                <div key={week} className="bg-white border border-stone-100 rounded-xl p-4" data-testid={`week-${week}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-stone-700">{week}</span>
                    <div className="flex gap-3 text-xs">
                      <span className="text-seawater-600">{data.used.length} brukt</span>
                      <span className="text-red-500">{data.wasted.length} svinn</span>
                    </div>
                  </div>
                  <div className="w-full bg-stone-100 rounded-full h-2">
                    <div className="bg-seawater-500 h-2 rounded-full transition-all" style={{ width: `${usedPct}%` }} />
                  </div>
                  {data.wasted.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {data.wasted.map((w) => (
                        <div key={w.id} className="text-xs text-red-500 flex items-center gap-1">
                          <Trash2 className="h-3 w-3" />
                          {w.item_name} {w.amount && `(${w.amount} ${w.unit || ""})`} – {w.reason}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
